// @systeme: interface
// « Ce qui revient » : la liste des récurrences, avec leur prochaine date, un
// interrupteur pour mettre en pause, et l'accès à la création.
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { ChevronLeft, Plus, Trash2 } from 'lucide-react';
import { Link as LienRouteur } from 'react-router';
import type { Recurrence } from '@foyer/shared';
import { Chargement } from '../composants/Chargement';
import { useMessages } from '../composants/Messages';
import { api } from '../lib/api';
import { formaterJourCourt, jourDe } from '../lib/dates';
import { couleurs, tailles } from '../theme/theme';

export function Recurrences() {
  const { dire, direErreur } = useMessages();
  const [liste, setListe] = useState<Recurrence[] | undefined>();

  useEffect(() => {
    api.recurrences.lister().then(setListe).catch(direErreur);
  }, [direErreur]);

  async function recharger() {
    setListe(await api.recurrences.lister());
  }

  async function basculer(r: Recurrence, active: boolean) {
    try {
      await api.recurrences.modifier(r.id, { active });
      await recharger();
      dire(active ? 'C’est reparti.' : 'En pause. Les prochaines occurrences sont retirées.');
    } catch (e) {
      direErreur(e);
    }
  }

  async function supprimer(r: Recurrence) {
    try {
      await api.recurrences.supprimer(r.id);
      await recharger();
      dire('Supprimé.');
    } catch (e) {
      direErreur(e);
    }
  }

  if (!liste) return <Chargement />;

  return (
    <Box sx={{ px: `${tailles.marge}px`, pt: 3 }}>
      <Button
        component={LienRouteur}
        to="/a-venir"
        startIcon={<ChevronLeft size={18} />}
        color="inherit"
        sx={{ px: 0, mb: 1 }}
      >
        À venir
      </Button>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h1" component="h1">
          Ce qui revient
        </Typography>
        <Button
          component={LienRouteur}
          to="/a-venir/recurrences/nouvelle"
          variant="contained"
          startIcon={<Plus size={18} />}
        >
          Ajouter
        </Button>
      </Stack>

      {liste.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          Poubelles, factures, entretien… Ajoute ce qui revient, et n’y pense plus.
        </Typography>
      ) : (
        <List disablePadding>
          {liste.map((r) => (
            <ListItem
              key={r.id}
              sx={{
                bgcolor: couleurs.blanc,
                borderRadius: `${tailles.rayon}px`,
                mb: 1,
                border: 1,
                borderColor: 'divider',
                pr: 1,
              }}
              secondaryAction={
                <Stack direction="row" alignItems="center">
                  <Switch
                    checked={r.active}
                    onChange={(e) => void basculer(r, e.target.checked)}
                    inputProps={{ 'aria-label': `Activer ${r.gabarit.titre}` }}
                  />
                  <IconButton
                    aria-label={`Supprimer ${r.gabarit.titre}`}
                    onClick={() => void supprimer(r)}
                  >
                    <Trash2 size={20} />
                  </IconButton>
                </Stack>
              }
            >
              <ListItemText
                primary={r.gabarit.titre}
                secondary={`${r.description}${r.prochaineOccurrence ? ` · prochaine : ${formaterJourCourt(jourDe(r.prochaineOccurrence))}` : r.active ? '' : ' · en pause'}`}
                sx={{ pr: 10 }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
}
